<template>
  <PopupButton :placement="'auto'" :offset="6" :renderOnShow="true">
    <template #button>
      <div class="btn btn-neutral btn-sm">Select</div>
    </template>
    <template #popup>
      <div class="m-4 flex flex-col gap-3">
        <span class="text-center text-sm">
          Click on keys or hold left mouse button and hover over them.
        </span>

        <KeysSelect
          :width="600"
          :height="2000"
          :isSelected="(r, c) => selectedKeys.has(r, c)"
          @keyClicked="toggleKey"
        />

        <div class="flex justify-center items-center gap-1">
          <button class="btn btn-neutral btn-sm" @click="setAll(() => true)">
            All
          </button>
          <button class="btn btn-neutral btn-sm" @click="setAll(() => false)">
            Clear
          </button>
          <button
            class="btn btn-neutral btn-sm"
            @click="setAll((k) => k.col < kb.columns / 2)"
          >
            Left
          </button>
          <button
            class="btn btn-neutral btn-sm"
            @click="setAll((k) => k.col >= kb.columns / 2)"
          >
            Right
          </button>
        </div>
      </div>
    </template>
  </PopupButton>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Keys } from "@/keyboard";
import { fromSelectedKeys, toSelectedKeys } from "@/keyboard/keys_utils";
import { useKeyboardStore, useLayoutStore } from "@/stores";
import PopupButton from "@/components/utils/PopupButton.vue";
import KeysSelect from "@/components/leds/KeysSelect.vue";

const props = defineProps<{
  modelValue?: Keys | null; // Null means "all"
}>();

const emit = defineEmits<{
  (_e: "update:modelValue", _value: Keys | null): void;
}>();

const kb = useKeyboardStore();
const layout = useLayoutStore();

const keys = computed(() =>
  props.modelValue == undefined ? null : props.modelValue
);
const selectedKeys = ref(toSelectedKeys(keys.value));
watch(keys, (newKeys) => (selectedKeys.value = toSelectedKeys(newKeys)));

const toggleKey = (row: number, col: number) => {
  selectedKeys.value.has(row, col)
    ? selectedKeys.value.remove(row, col)
    : selectedKeys.value.add(row, col);
  if (selectedKeys.value.count > 0)
    emit("update:modelValue", fromSelectedKeys(selectedKeys.value));
};

type Key = ReturnType<typeof layout.layout.iter>[0];
const setAll = (selected: (key: Key) => boolean) => {
  layout.layout
    .iter()
    .forEach((key) =>
      selected(key)
        ? selectedKeys.value.add(key.row, key.col)
        : selectedKeys.value.remove(key.row, key.col)
    );
  if (selectedKeys.value.count > 0)
    emit("update:modelValue", fromSelectedKeys(selectedKeys.value));
};
</script>
